import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { Modal, Button, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTimes,
  faPencilAlt,
  faCheck,
  faRobot,
  faTrash,
  faExclamationTriangle,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import {
  anchovy,
  backgroundGrey,
  blackberry,
  bootstrapDanger,
  grape,
  greyBorder,
  lightPurple,
  mutedGray,
  positiveGreen,
  softAmber,
  softAmberLight,
  softAmberText,
  squidInk,
  standardGray,
  white,
  whiteTransparent15,
  whiteTransparent40,
  whiteTransparent70,
} from 'lib/css/colors';
import { L4_CATEGORY_DIVISION_RESEGMENT_BY_ATTRIBUTE } from 'lib/networking/endpoints';
import { confirmRichAction, SwalToast } from 'lib/utils/toast';
import usePost from 'lib/hooks/usePost';

function AttributeTermsEditModal({ show, onHide, attributeType, category, initialValues }) {
  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [attributeName, setAttributeName] = useState(attributeType);
  const [editingAttributeName, setEditingAttributeName] = useState(false);
  const [attributeNameDraft, setAttributeNameDraft] = useState('');
  const wasVisible = useRef(false);

  const { postData: resegmentByAttribute, loading: sending } = usePost(
    L4_CATEGORY_DIVISION_RESEGMENT_BY_ATTRIBUTE,
  );

  // Only reset state when the modal transitions from closed → open.
  // initialValues and attributeType are intentionally excluded from deps — we only
  // want to read them at open-time, not re-initialise on every parent re-render
  // (which would silently discard in-progress edits when a new array reference is created).
  useEffect(() => {
    if (show && !wasVisible.current) {
      setTerms(initialValues?.map(v => v.name || v) ?? []);
      setNewTerm('');
      setEditingIndex(null);
      setEditingValue('');
      setAttributeName(attributeType);
      setEditingAttributeName(false);
      setAttributeNameDraft('');
    }
    wasVisible.current = show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleAddTerm = () => {
    const trimmed = newTerm.trim();
    if (!trimmed) return;
    if (terms.includes(trimmed)) {
      SwalToast('warning', `"${trimmed}" already exists in the list`);
      return;
    }
    setTerms(prev => [...prev, trimmed]);
    setNewTerm('');
  };

  const handleDeleteTerm = index => {
    setTerms(prev => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditingValue('');
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(prev => prev - 1);
    }
  };

  const handleStartEdit = (index, value) => {
    setEditingIndex(index);
    setEditingValue(value);
  };

  const handleConfirmEdit = index => {
    const trimmed = editingValue.trim();
    if (!trimmed) return;
    if (terms.some((t, i) => t === trimmed && i !== index)) {
      SwalToast('warning', `"${trimmed}" already exists in the list`);
      return;
    }
    setTerms(prev => prev.map((t, i) => (i === index ? trimmed : t)));
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleSendToML = () => {
    if (terms.length === 0) {
      SwalToast('error', 'Please add at least one term before sending to AI/ML');
      return;
    }

    // eslint-disable-next-line space-before-function-paren
    const sendToML = async () => {
      try {
        const response = await resegmentByAttribute({
          l4_category_id: category.id,
          attribute_type: attributeName,
          attribute_values: terms,
        });

        if (response?.error) {
          throw response.error;
        }

        SwalToast(
          'success',
          `Re-segmentation task queued for "${attributeName}" in ${category.category_name}`,
        );
        onHide();
      } catch {
        SwalToast('error', 'Failed to queue re-segmentation task. Please try again.');
      }
    };

    confirmRichAction(
      sendToML,
      `<p>This will:</p>
       <ol style="text-align:left; margin: 0 auto; display: inline-block;">
         <li>Delete <strong>all</strong> existing segmentation records for
           <strong>${category.category_name}</strong> from Snowflake</li>
         <li>Re-run AI segmentation on <strong>${attributeName}</strong> only,
           using <strong>${terms.length} defined terms</strong></li>
       </ol>
       <p class="mt-3 mb-0">
         <small>All attribute records for this category will be cleared.
         This action will be queued as a background task.</small>
       </p>`,
      'Confirm Re-segmentation',
      'Yes, send to AI/ML',
    );
  };

  const handleNewTermKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTerm();
    }
  };

  const handleStartEditAttributeName = () => {
    setAttributeNameDraft(attributeName);
    setEditingAttributeName(true);
  };

  const handleConfirmAttributeName = () => {
    const trimmed = attributeNameDraft.trim();
    if (trimmed) setAttributeName(trimmed);
    setEditingAttributeName(false);
  };

  const handleAttributeNameKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirmAttributeName();
    }
    if (e.key === 'Escape') {
      setEditingAttributeName(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className={css(styles.modalHeader)}>
        <Modal.Title className={css(styles.modalTitle)}>
          <span className={css(styles.attributeLabel)}>Edit Attribute Terms</span>
          {editingAttributeName ? (
            <div className={css(styles.attributeNameEdit)}>
              <Form.Control
                type="text"
                value={attributeNameDraft}
                onChange={e => setAttributeNameDraft(e.target.value)}
                onKeyDown={handleAttributeNameKeyDown}
                className={css(styles.attributeNameInput)}
                autoFocus
                aria-label="Edit attribute name"
              />
              <Button
                variant="link"
                onClick={handleConfirmAttributeName}
                aria-label="Confirm attribute name"
                className={css(styles.headerIconBtn, styles.headerConfirmBtn)}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                variant="link"
                onClick={() => setEditingAttributeName(false)}
                aria-label="Cancel attribute name edit"
                className={css(styles.headerIconBtn, styles.headerCancelBtn)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <Button
              variant="link"
              onClick={handleStartEditAttributeName}
              className={css(styles.attributeBadgeBtn)}
              title="Click to edit attribute name"
              aria-label={`Edit attribute name: ${attributeName}`}
            >
              <FontAwesomeIcon icon={faTag} className={css(styles.attributeBadgeIcon)} />
              {attributeName}
              <FontAwesomeIcon icon={faPencilAlt} className={css(styles.attributeEditIcon)} />
            </Button>
          )}
        </Modal.Title>
        <Button
          variant="link"
          onClick={onHide}
          aria-label="Close modal"
          className={css(styles.closeBtn)}
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </Modal.Header>

      <Modal.Body className={css(styles.modalBody)}>
        <div className={css(styles.categoryInfo)}>
          <span className={css(styles.categoryInfoLabel)}>Category:</span>
          <span className={css(styles.categoryInfoValue)}>{category?.category_name}</span>
        </div>

        <Alert className={css(styles.warningAlert)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          <strong>All</strong> existing segmentation records for this category will be deleted from
          Snowflake. The AI/ML server will then re-segment on <strong>{attributeName}</strong> only,
          using the terms defined below.
        </Alert>

        <div className={css(styles.addTermRow)}>
          <Form.Control
            type="text"
            placeholder="Add a new term (e.g. Spicy, Vegetarian)..."
            value={newTerm}
            onChange={e => setNewTerm(e.target.value)}
            onKeyDown={handleNewTermKeyDown}
            className={css(styles.addInput)}
          />
          <Button
            variant="outline-primary"
            onClick={handleAddTerm}
            disabled={!newTerm.trim()}
            className={css(styles.addBtn)}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Add
          </Button>
        </div>

        <div className={css(styles.termsSection)}>
          <div className={css(styles.termsSectionHeader)}>
            <span className={css(styles.termsSectionTitle)}>
              Terms{' '}
              <Badge variant="secondary" className={css(styles.countBadge)}>
                {terms.length}
              </Badge>
            </span>
            {terms.length > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setTerms([])}
                className={css(styles.clearAllBtn)}
              >
                Clear all
              </Button>
            )}
          </div>

          {terms.length === 0 ? (
            <div className={css(styles.emptyTerms)}>
              No terms defined. Add terms above to specify segmentation values.
            </div>
          ) : (
            <div className={css(styles.termsList)}>
              {terms.map((term, index) => (
                <div key={index} className={css(styles.termItem)}>
                  {editingIndex === index ? (
                    <div className={css(styles.editRow)}>
                      <Form.Control
                        type="text"
                        value={editingValue}
                        onChange={e => setEditingValue(e.target.value)}
                        onKeyDown={e => handleKeyDown(e, () => handleConfirmEdit(index))}
                        className={css(styles.editInput)}
                        autoFocus
                      />
                      <Button
                        variant="link"
                        onClick={() => handleConfirmEdit(index)}
                        aria-label="Confirm edit"
                        className={css(styles.iconBtn, styles.confirmBtn)}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>
                      <Button
                        variant="link"
                        onClick={handleCancelEdit}
                        aria-label="Cancel edit"
                        className={css(styles.iconBtn, styles.cancelBtn)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  ) : (
                    <div className={css(styles.termDisplay)}>
                      <span className={css(styles.termName)}>{term}</span>
                      <div className={css(styles.termActions)}>
                        <Button
                          variant="link"
                          onClick={() => handleStartEdit(index, term)}
                          aria-label={`Edit term ${term}`}
                          className={css(styles.iconBtn, styles.editBtn)}
                        >
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => handleDeleteTerm(index)}
                          aria-label={`Delete term ${term}`}
                          className={css(styles.iconBtn, styles.deleteBtn)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className={css(styles.modalFooter)}>
        <Button variant="outline-secondary" onClick={onHide} disabled={sending}>
          Close
        </Button>
        <Button
          onClick={handleSendToML}
          disabled={sending || terms.length === 0}
          className={css(styles.sendBtn)}
        >
          {sending ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Queuing task...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRobot} className="mr-2" />
              Send to AI/ML
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalHeader: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${lightPurple} 100%)`,
    color: white,
    borderBottom: 'none',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 600,
    color: white,
  },
  attributeLabel: {
    color: white,
  },
  attributeBadgeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: grape,
    color: white,
    fontSize: '13px',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: '12px',
    opacity: 0.95,
    ':hover': {
      backgroundColor: grape,
      color: white,
      opacity: 1,
    },
  },
  attributeBadgeIcon: {
    fontSize: '11px',
    opacity: 0.8,
  },
  attributeEditIcon: {
    fontSize: '10px',
    opacity: 0.7,
  },
  attributeNameEdit: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  attributeNameInput: {
    height: '30px',
    fontSize: '13px',
    width: '180px',
    padding: '2px 8px',
    backgroundColor: whiteTransparent15,
    border: `1px solid ${whiteTransparent40}`,
    color: white,
    borderRadius: '6px',
  },
  headerIconBtn: {
    padding: '2px 6px',
    lineHeight: 1,
  },
  headerConfirmBtn: {
    color: positiveGreen,
    ':hover': { color: positiveGreen },
  },
  headerCancelBtn: {
    color: whiteTransparent70,
    ':hover': { color: white },
  },
  closeBtn: {
    color: white,
    padding: '4px 8px',
    opacity: 0.8,
    ':hover': {
      opacity: 1,
      color: white,
    },
  },
  modalBody: {
    padding: '20px',
  },
  categoryInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '14px',
    padding: '10px 14px',
    backgroundColor: backgroundGrey,
    borderRadius: '6px',
    border: `1px solid ${greyBorder}`,
  },
  categoryInfoLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: mutedGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  categoryInfoValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: anchovy,
  },
  warningAlert: {
    backgroundColor: softAmberLight,
    borderColor: softAmber,
    color: softAmberText,
    fontSize: '13px',
    padding: '10px 14px',
    marginBottom: '16px',
  },
  addTermRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  addInput: {
    flex: 1,
    fontSize: '14px',
  },
  addBtn: {
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  termsSection: {
    border: `1px solid ${greyBorder}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  termsSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    backgroundColor: backgroundGrey,
    borderBottom: `1px solid ${greyBorder}`,
  },
  termsSectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: anchovy,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  countBadge: {
    fontSize: '11px',
  },
  clearAllBtn: {
    fontSize: '12px',
    color: bootstrapDanger,
    padding: '0',
    ':hover': {
      color: bootstrapDanger,
      textDecoration: 'underline',
    },
  },
  emptyTerms: {
    padding: '24px',
    textAlign: 'center',
    color: standardGray,
    fontSize: '14px',
  },
  termsList: {
    maxHeight: '320px',
    overflowY: 'auto',
  },
  termItem: {
    borderBottom: `1px solid ${greyBorder}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  termDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderLeft: '3px solid transparent',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    ':hover': {
      backgroundColor: backgroundGrey,
      borderLeftColor: grape,
    },
  },
  termName: {
    fontSize: '15px',
    fontWeight: 500,
    color: squidInk,
    flex: 1,
  },
  termActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  editRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
  },
  editInput: {
    flex: 1,
    fontSize: '14px',
    height: '34px',
  },
  iconBtn: {
    padding: '4px 8px',
    lineHeight: 1,
  },
  confirmBtn: {
    color: positiveGreen,
    ':hover': {
      color: positiveGreen,
    },
  },
  cancelBtn: {
    color: standardGray,
    ':hover': {
      color: standardGray,
    },
  },
  editBtn: {
    color: grape,
    ':hover': {
      color: grape,
    },
  },
  deleteBtn: {
    color: bootstrapDanger,
    ':hover': {
      color: bootstrapDanger,
    },
  },
  modalFooter: {
    borderTop: `1px solid ${greyBorder}`,
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  sendBtn: {
    backgroundColor: blackberry,
    borderColor: blackberry,
    color: white,
    fontWeight: 500,
    ':disabled': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      opacity: 0.65,
    },
  },
});

AttributeTermsEditModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  attributeType: PropTypes.string.isRequired,
  category: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category_name: PropTypes.string.isRequired,
  }).isRequired,
  initialValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ name: PropTypes.string })]),
  ),
};

export default AttributeTermsEditModal;
